import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, ArrowLeft, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Cart = () => {
  const { cartItems, cartTotal, updateQuantity, removeFromCart } = useCart();
  const { user } = useAuth();
  const { t } = useLanguage();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const getCategoryName = (category) => {
    if (!category) return '';
    switch(category) {
      case 'dewalt-batteries': return t.nav.powerTools;
      case 'milwaukee-batteries': return t.nav.handTools;
      case 'makita-batteries': return t.nav.accessories;
      case 'tools-accessories': return t.nav.toolsAccessories;
      default: return category;
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen" data-testid="cart-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag size={64} className="mx-auto text-zinc-700 mb-6" />
            <h1 className="font-heading text-4xl uppercase text-white mb-4">{t.cart.empty}</h1>
            <p className="text-zinc-400 mb-8">{t.cart.emptyDesc}</p>
            <Link to="/shop" className="bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors inline-flex items-center gap-2" data-testid="continue-shopping-btn">
              {t.cart.continueShopping}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} /> {t.cart.continueShopping}
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white" data-testid="cart-title">
            {t.cart.title}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="border border-zinc-800">
              {cartItems.map((item, index) => (
                <div key={item.product_id || item.product?.id} className={`flex gap-4 p-4 md:p-6 ${index !== cartItems.length - 1 ? 'border-b border-zinc-800' : ''}`} data-testid={`cart-item-${item.product_id || item.product?.id}`}>
                  <Link to={`/product/${item.product?.id}`} className="w-24 h-24 md:w-32 md:h-32 bg-[#111111] flex-shrink-0">
                    {item.product?.image_url ? (
                      <img src={item.product.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No Image</div>
                    )}
                  </Link>

                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-mono text-xs tracking-widest uppercase text-yellow-500">
                          {getCategoryName(item.product?.category)}
                        </span>
                        <Link to={`/product/${item.product?.id}`}>
                          <h3 className="font-heading text-xl uppercase text-white hover:text-yellow-400 transition-colors">{item.product?.name}</h3>
                        </Link>
                      </div>
                      <button onClick={() => removeFromCart(item.product_id || item.product?.id)} className="p-2 text-zinc-500 hover:text-red-500 transition-colors" data-testid="remove-item-btn">
                        <Trash2 size={20} />
                      </button>
                    </div>

                    <div className="mt-auto flex items-end justify-between">
                      <div className="flex items-center border border-zinc-700 bg-[#111111]">
                        <button onClick={() => updateQuantity(item.product_id || item.product?.id, item.quantity - 1)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid="cart-quantity-decrease">
                          <Minus size={16} />
                        </button>
                        <span className="px-4 font-mono text-white" data-testid="cart-quantity">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id || item.product?.id, item.quantity + 1)} className="p-2 text-zinc-400 hover:text-white transition-colors" data-testid="cart-quantity-increase">
                          <Plus size={16} />
                        </button>
                      </div>

                      <span className="font-mono text-xl text-yellow-500" data-testid="cart-item-total">
                        {formatPrice((item.product?.price || 0) * item.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="border border-zinc-800 bg-[#111111] p-6 sticky top-24">
              <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.cart.orderSummary}</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-zinc-400">
                  <span>{t.cart.subtotal}</span>
                  <span className="font-mono">{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>{t.cart.shipping}</span>
                  <span className="font-mono">{cartTotal >= 500 ? t.cart.free : formatPrice(29.99)}</span>
                </div>
                {cartTotal < 500 && (
                  <p className="text-xs text-zinc-500">
                    {t.cart.addMore} {formatPrice(500 - cartTotal)} {t.cart.forFreeShipping}
                  </p>
                )}
              </div>

              <div className="border-t border-zinc-700 pt-4 mb-6">
                <div className="flex justify-between text-white">
                  <span className="font-bold">{t.cart.total}</span>
                  <span className="font-mono text-2xl text-yellow-500" data-testid="cart-total">
                    {formatPrice(cartTotal + (cartTotal >= 500 ? 0 : 29.99))}
                  </span>
                </div>
              </div>

              <Link to="/checkout" className="w-full bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors flex items-center justify-center mb-3" data-testid="checkout-btn">
                {t.cart.proceedToCheckout}
              </Link>
              
              {!user && (
                <p className="text-center text-sm text-zinc-500">
                  {t.cart.orLoginText || 'lub'}{' '}
                  <Link to="/login" className="text-yellow-400 hover:underline">{t.cart.loginToCheckout}</Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
